import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hideNotification, showNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../notifications/constants';
import { isRenderStatsOpen } from '../../functions';
import { startRenderMonitor, stopRenderMonitor } from '../../renderMonitor';
import { REFRESH_MS, buildRenderStatsHtml, buildRenderStatsText } from '../../renderStatsPanel';
/**
 * Identifies the fallback notification so each update replaces the last one
 * instead of stacking a new toast every few seconds.
 */
const NOTIFICATION_UID = 'perf-debug.render-stats';
/** How often the fallback notification is refreshed, in ms. */
const NOTIFY_MS = 5000;
/**
 * Where the panel sits.
 *
 * Rendered inside the conference's own React tree rather than appended to
 * document.body. A hand-placed body node is not reliably visible on every
 * device: while anything is fullscreen the browser paints only that element's
 * subtree, and on mobile layouts Jitsi puts parts of its own UI through
 * JitsiPortal instead. Living in the tree means the panel inherits whatever
 * already makes the rest of the interface visible there.
 *
 * The size is clamped to the viewport so a narrow or zoomed tablet cannot push
 * the contents out of reach.
 */
const PANEL_STYLE = {
    position: 'fixed',
    right: 12,
    top: 54,
    zIndex: 99999,
    width: 280,
    maxWidth: 'calc(100vw - 24px)',
    maxHeight: 'calc(100vh - 120px)',
    overflow: 'auto',
    boxSizing: 'border-box',
    padding: '10px 12px',
    // No backdrop-filter: on several tablet browsers it is unsupported or
    // renders the layer blank, and an unreadable debug panel is worth nothing.
    background: 'rgba(16,18,24,.94)',
    border: '1px solid rgba(255,255,255,.14)',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,.45)',
    color: '#e6e9ef',
    font: '11px/1.45 ui-monospace,monospace',
    pointerEvents: 'none',
    userSelect: 'none'
};
/**
 * The 描画計測 panel: frame rate, long tasks, input latency and page size, for
 * machines where the page as a whole draws slowly.
 *
 * @returns {JSX.Element|null}
 */
const RenderStatsPanel = () => {
    const open = useSelector(isRenderStatsOpen);
    const dispatch = useDispatch();
    const ref = useRef(null);
    console.info(`[perf-dbg] コンポーネントが描画されました open=${open}`);
    useEffect(() => {
        console.info(`[perf-dbg] useEffect open=${open} ref=${ref.current ? 'あり' : '無し'}`);
        if (!open) {
            return;
        }
        // Measurement runs only while the panel is up: a rAF loop and two
        // PerformanceObservers are not free, and this exists for machines that
        // cannot spare the work.
        startRenderMonitor();
        // Written imperatively rather than through state, so the once-a-second
        // refresh costs one innerHTML assignment instead of a React render
        // pass — the panel must not show up in its own measurements.
        const paint = () => {
            if (ref.current) {
                ref.current.innerHTML = buildRenderStatsHtml();
            }
        };
        paint();
        // 見えない理由を要素自身に言わせる。レイアウトが確定してから測る。
        const reportTimer = setTimeout(() => {
            const el = ref.current;
            if (!el) {
                console.info('[perf-dbg] ★要素が存在しません');
                return;
            }
            const r = el.getBoundingClientRect();
            const cs = getComputedStyle(el);
            const chain = [];
            let q = el.parentElement;
            // 祖先に transform / filter があると position:fixed の基準が画面では
            // なくその祖先になる。display:none や overflow で切られていても
            // ここに出る。
            for (let i = 0; i < 8 && q; i++) {
                const s2 = getComputedStyle(q);
                const odd = [
                    s2.display === 'none' && 'display:none',
                    s2.visibility === 'hidden' && 'visibility:hidden',
                    s2.opacity === '0' && 'opacity:0',
                    s2.transform !== 'none' && 'transform有',
                    s2.filter !== 'none' && 'filter有',
                    s2.overflow !== 'visible' && `overflow:${s2.overflow}`
                ].filter(Boolean);
                chain.push(`${q.tagName.toLowerCase()}${q.id ? '#' + q.id : ''}`
                    + (odd.length ? `[${odd.join(',')}]` : ''));
                q = q.parentElement;
            }
            console.info('[perf-dbg] パネルの状態\n'
                + `  位置サイズ : left=${Math.round(r.left)} top=${Math.round(r.top)} `
                + `${Math.round(r.width)}x${Math.round(r.height)}\n`
                + `  画面サイズ : ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio}\n`
                + `  見た目     : display=${cs.display} visibility=${cs.visibility} `
                + `opacity=${cs.opacity} z-index=${cs.zIndex} position=${cs.position}\n`
                + `  中身の長さ : ${el.innerHTML.length}文字\n`
                + `  DOM接続    : ${el.isConnected}  全画面要素=${document.fullscreenElement
                    ? document.fullscreenElement.tagName : '無し'}\n`
                + `  親をたどる : ${chain.join(' < ')}`);
        }, 500);
        // 数値もコンソールへ。パネルが見えなくても調査は進む。
        const logTimer = setInterval(() => console.info('[perf-dbg] ' + buildRenderStatsText().replace(/\n/g, ' / ')), 5000);
        const timer = setInterval(paint, REFRESH_MS);
        // Same figures through the app's own notification system, for devices
        // where this overlay cannot be seen. Replaced rather than appended, so
        // it reads as one live readout instead of a pile of toasts.
        const notify = () => {
            dispatch(hideNotification(NOTIFICATION_UID));
            dispatch(showNotification({
                uid: NOTIFICATION_UID,
                title: '描画計測',
                description: buildRenderStatsText()
            }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
        };
        // One window of samples has to accumulate before the numbers mean
        // anything, so the first readout waits rather than reporting zeros.
        const firstNotify = setTimeout(notify, 2000);
        const notifyTimer = setInterval(notify, NOTIFY_MS);
        return () => {
            clearInterval(timer);
            clearTimeout(reportTimer);
            clearInterval(logTimer);
            clearTimeout(firstNotify);
            clearInterval(notifyTimer);
            dispatch(hideNotification(NOTIFICATION_UID));
            stopRenderMonitor();
        };
    }, [open]);
    if (!open) {
        return null;
    }
    return (React.createElement("div", { ref: ref, style: PANEL_STYLE }));
};
export default RenderStatsPanel;
